import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";

import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { Company } from "../../../../../../../models/company";
import { Currency } from "../../../../../../../models/currency";
import { Searches, Filters, Sorts, Sort } from "../../../../../../../models/shipeezi.table";
import CompanyService from "../../../../../../../services/company.service";
import CurrencyService from "../../../../../../../services/currency.service";
import { useAppState } from "../../../../../../../context/StateContext";

const useActions = () => {
  const { currentUser } = useAppState();

  const [company, setCompany] = useState<Company>();
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  useQuery(["get.company"], () => CompanyService.getById(currentUser?.companyId || 0), {
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response && response.data) setCompany(response.data);
    },
  });

  const { refetch, isFetching } = useQuery(
    ["currencies.list", page, pageSize, filters, searches, sorting],
    () =>
      CurrencyService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify(sorting) : "",
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setCurrencies(response.data);
      },
    }
  );

  const { refetch: refetchCurrenciesTotal } = useQuery(
    ["currencies.total", filters, searches],
    () =>
      CurrencyService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
      },
    }
  );

  const { mutate: updateCurrency, isLoading: isUpdating } = useMutation(
    ["currency.update"],
    ({ id, currency }: { id: number; currency: Currency }) => CurrencyService.update(id, currency),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchCurrenciesTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data);
        setHasErrors(true);
      },
    }
  );

  const { mutate: createCurrency, isLoading: isCreating } = useMutation(
    ["currency.create"],
    (currency: Currency) => CurrencyService.create(currency),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchCurrenciesTotal();
      },
      onError: (error: any) => {
        NotificationToast.error(error.response.data);
        setHasErrors(true);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    company,
    currencies,
    hasErrors,
    page,
    total,
    pageSize,
    isLoading: isFetching || isCreating || isUpdating,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createCurrency,
    updateCurrency,
  };
};

export default useActions;
